import {
  BaseInsuranceCoverage,
  CoverageType,
  defaultStageValidate,
  FieldMap,
  FieldMapValue,
  getHasPrimaryCoverage,
  getHasSecondaryCoverage,
  getInsuranceCoverageField,
  getPregnancyCoverageType,
  InsuranceInfo,
  UploadsProgress,
  ValidationErrors,
} from '@hb/shared'
import { PopUpMessageContext, processFieldMapData, useFormWizard } from '@hb/shared-frontend'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { getInsuranceInfoValidationField } from './field'
import { OnInsuranceInfoSubmit } from './types'

interface CoverageStatus {
  data: Partial<BaseInsuranceCoverage>
  hasCoverage: boolean
  field: FieldMap
  type: CoverageType | null
  errors: ValidationErrors
  complete: boolean
  onEdit: () => void
  onCancel: () => void
}

export interface InsuranceInfoStageContextType {
  isSaving: boolean
  onUpdate: OnInsuranceInfoSubmit
  onSaveChanges: (updates?: Partial<InsuranceInfo>) => Promise<void>
  uploads: UploadsProgress
  insuranceInfoData: Partial<InsuranceInfo>
  hasChanges: boolean
  primaryCoverage: CoverageStatus
  secondaryCoverage: CoverageStatus
}

export const InsuranceInfoStageContext = createContext<InsuranceInfoStageContextType>({
  isSaving: false,
  onUpdate: () => Promise.resolve(),
  onSaveChanges: () => Promise.resolve(),
  hasChanges: false,
  uploads: {},
  insuranceInfoData: {},
  primaryCoverage: {
    data: {},
    onEdit: () => {},
    onCancel: () => {},
    hasCoverage: false,
    field: { name: '', children: {} },
    type: null,
    errors: undefined,
    complete: false,
  },
  secondaryCoverage: {
    data: {},
    onEdit: () => {},
    onCancel: () => {},
    hasCoverage: false,
    field: { name: '', children: {} },
    type: null,
    errors: undefined,
    complete: false,
  },
})

export const InsuranceInfoStageProvider = ({ children }: { children: React.ReactNode }) => {
  const { data, onSubmitStage, close, readOnly, storagePath } = useFormWizard()

  const [insuranceInfoData, setInsuranceInfoData] = useState<Partial<InsuranceInfo>>({
    ...data?.insuranceInfo,
  })

  const { showMessage } = useContext(PopUpMessageContext)
  const [uploads, setUploads] = useState<UploadsProgress>({})
  const [isSaving, setIsSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    if (data?.insuranceInfo && !hasChanges) {
      setInsuranceInfoData(data.insuranceInfo)
    }
  }, [data, hasChanges])

  const hasPrimaryCoverage = useMemo(
    () => getHasPrimaryCoverage(insuranceInfoData),
    [insuranceInfoData],
  )

  const primaryCoverageType = useMemo(
    () => getPregnancyCoverageType('primary', insuranceInfoData),
    [insuranceInfoData],
  )

  const primaryCoverageField = useMemo(
    () => getInsuranceCoverageField(primaryCoverageType === 'medicaid'),
    [primaryCoverageType],
  )

  const primaryCoverageErrors = useMemo(() => {
    if (!primaryCoverageType) return undefined
    return defaultStageValidate(primaryCoverageField)(insuranceInfoData?.primaryCoverage ?? {})
  }, [primaryCoverageType, insuranceInfoData, primaryCoverageField])

  const hasSecondaryCoverage = useMemo(() => {
    if (!primaryCoverageType) return false
    return getHasSecondaryCoverage(insuranceInfoData)
  }, [insuranceInfoData, primaryCoverageType])

  const secondaryCoverageType = useMemo(
    () => getPregnancyCoverageType('secondary', insuranceInfoData),
    [insuranceInfoData],
  )

  const secondaryCoverageField = useMemo(
    () => getInsuranceCoverageField(secondaryCoverageType === 'medicaid'),
    [secondaryCoverageType],
  )

  const secondaryCoverageErrors = useMemo(
    () => defaultStageValidate(secondaryCoverageField)(insuranceInfoData?.secondaryCoverage ?? {}),
    [insuranceInfoData, secondaryCoverageField],
  )

  const [primaryCoverageComplete, setPrimaryCoverageComplete] = useState(
    hasPrimaryCoverage && !primaryCoverageErrors,
  )

  const [secondaryCoverageComplete, setSecondaryCoverageComplete] = useState(
    hasSecondaryCoverage && !secondaryCoverageErrors,
  )

  const onUpdate = useCallback<OnInsuranceInfoSubmit>(
    async (submitted: FieldMapValue, merge = true, forceSave = false) => {
      if (readOnly) return
      if (!storagePath) {
        showMessage({
          text: 'Something went wrong',
          subText: 'Unable to get storage path for files',
          type: 'error',
        })
        return
      }
      const updated = merge ? { ...insuranceInfoData, ...submitted } : submitted
      const newValidationField = getInsuranceInfoValidationField(updated, false)
      const errors = defaultStageValidate(newValidationField)(updated)
      setIsSaving(true)
      // setInsuranceInfoData(updated)
      // try {
      if (!errors || forceSave) {
        const processed = await processFieldMapData(
          storagePath,
          newValidationField,
          updated,
          insuranceInfoData,
          setUploads,
        )
        await onSubmitStage(processed, setUploads, undefined, false)
        setInsuranceInfoData(processed)
        setHasChanges(false)
        if (!errors) close()
      } else {
        setInsuranceInfoData(updated)
        setHasChanges(true)
      }
      setPrimaryCoverageComplete(!!updated.primaryCoverage && !errors?.primaryCoverage)
      setSecondaryCoverageComplete(!!updated.secondaryCoverage && !errors?.secondaryCoverage)
      // } catch (err: any) {
      //   showMessage({
      //     text: 'Error updating insurance information',
      //     subText: err.message,
      //     type: 'error',
      //   })
      // }
      setIsSaving(false)
    },
    [onSubmitStage, showMessage, insuranceInfoData, close, readOnly, storagePath],
  )

  const primaryCoverage = useMemo<CoverageStatus>(
    () => ({
      hasCoverage: hasPrimaryCoverage,
      field: primaryCoverageField,
      type: primaryCoverageType,
      data: insuranceInfoData.primaryCoverage ?? {},
      errors: primaryCoverageErrors,
      complete: primaryCoverageComplete,
      onEdit: () => setPrimaryCoverageComplete(false),
      onCancel: () => setPrimaryCoverageComplete(true),
    }),
    [
      hasPrimaryCoverage,
      primaryCoverageField,
      primaryCoverageType,
      insuranceInfoData,
      primaryCoverageErrors,
      primaryCoverageComplete,
    ],
  )

  const secondaryCoverage = useMemo(
    () => ({
      hasCoverage: hasSecondaryCoverage,
      field: secondaryCoverageField,
      type: secondaryCoverageType,
      data: insuranceInfoData.secondaryCoverage ?? {},
      errors: secondaryCoverageErrors,
      complete: secondaryCoverageComplete,
      onEdit: () => setSecondaryCoverageComplete(false),
      onCancel: () => setSecondaryCoverageComplete(true),
    }),
    [
      hasSecondaryCoverage,
      secondaryCoverageField,
      secondaryCoverageType,
      secondaryCoverageErrors,
      secondaryCoverageComplete,
      insuranceInfoData,
    ],
  )

  const onSaveChanges = useCallback(
    async (updates?: Partial<InsuranceInfo>) => {
      await onSubmitStage({ ...insuranceInfoData, ...updates }, setUploads, undefined, false)
      setHasChanges(false)
    },
    [onSubmitStage, insuranceInfoData],
  )

  const contextData = useMemo<InsuranceInfoStageContextType>(
    () => ({
      isSaving,
      onUpdate,
      uploads,
      insuranceInfoData,
      onSaveChanges,
      hasChanges,
      primaryCoverage,
      secondaryCoverage,
    }),
    [
      isSaving,
      onUpdate,
      uploads,
      insuranceInfoData,
      onSaveChanges,
      hasChanges,
      primaryCoverage,
      secondaryCoverage,
    ],
  )
  return (
    <InsuranceInfoStageContext.Provider value={contextData}>
      {children}
    </InsuranceInfoStageContext.Provider>
  )
}

export const useInsuranceInfoStage = () => useContext(InsuranceInfoStageContext)
